
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Plus, Printer, FileText, FolderOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { printStudentsListPaginatedAsJPG } from "@/utils/printing/studentListPaginatedPrinter";
import { useStudents } from "@/contexts";
import { toast } from "sonner";
import { getBrowserInfo } from "@/utils/printing/printingBridge";
import { DocumentUploadDialog } from "./DocumentUploadDialog";
import PageSelectionDialog from "./PageSelectionDialog";
import StudentDownloadFormatDialog from "./StudentDownloadFormatDialog";

interface StudentsHeaderProps {
  onSearch?: (query: string) => void;
}

const StudentsHeader: React.FC<StudentsHeaderProps> = ({ onSearch }) => {
  const { students } = useStudents();
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);

  const handlePrintList = async (format: 'jpg' | 'pdf', startPage: number, endPage: number) => {
    if (students) {
      try {
        console.log('Print environment:', getBrowserInfo());
        
        const studentsList = students.flatMap(student => 
          student.programs.map(program => ({
            id: student.id,
            name: student.name,
            surname: student.surname,
            fathersName: student.fathersName || 'N/A',
            programCategory: program.category || 'No Category',
            programTitle: program.title || 'No Title',
            fees: program.fee || 0,
            paymentStatus: program.feeStatus || 'pending',
            remainingDays: program.completionDate ? 
              Math.max(0, Math.floor((new Date(program.completionDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 0
          }))
        );
        
        if (format === 'jpg') {
          const { printStudentsListPaginatedAsJPG } = await import('@/utils/printing/studentListPaginatedPrinter');
          await printStudentsListPaginatedAsJPG(studentsList, startPage, endPage);
        } else {
          const { printStudentsListPaginatedAsPDF } = await import('@/utils/printing/studentListPaginatedPrinter');
          await printStudentsListPaginatedAsPDF(studentsList, startPage, endPage);
        }
      } catch (error) {
        console.error('Error downloading students list:', error);
        toast.error('Failed to download students list');
      }
    } else {
      toast.error('No students data to download');
    }
  };

  const handleDownloadClick = () => {
    const totalStudents = getTotalStudents();
    if (totalStudents === 0) {
      toast.error('No students to download');
      return;
    }

    const studentsPerPage = 12; // Updated from 14 to 12
    const totalPages = Math.ceil(totalStudents / studentsPerPage);

    // If only one page, show format dialog directly
    if (totalPages === 1) {
      setShowFormatDialog(true);
    } else {
      // Show page selection dialog first
      setIsPageSelectionOpen(true);
    }
  };

  const handleDirectFormatDownload = (format: 'jpg' | 'pdf') => {
    const totalStudents = getTotalStudents();
    const totalPages = Math.ceil(totalStudents / 12); // Updated from 14 to 12
    handlePrintList(format, 1, totalPages);
  };

  const getTotalStudents = () => {
    if (!students) return 0;
    return students.reduce((total, student) => total + student.programs.length, 0);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-nalc-dark dark:text-white">
            Students
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
            Manage student profiles and program enrollments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/documents/manage">
            <Button 
              variant="outline" 
              size="sm"
              className="h-8 px-3 text-xs"
            >
              <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
              Manage DOCs
            </Button>
          </Link>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsDocumentDialogOpen(true)}
            className="h-8 px-3 text-xs"
          >
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            Add DOC
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleDownloadClick}
            className="h-8 px-3 text-xs"
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" />
            Download List
          </Button>
          
          <Link to="/students/new">
            <Button size="sm" className="h-8 px-3 text-xs">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add New Student
            </Button>
          </Link>
        </div>
      </div>

      <DocumentUploadDialog 
        isOpen={isDocumentDialogOpen} 
        onClose={() => setIsDocumentDialogOpen(false)} 
      />

      <PageSelectionDialog
        isOpen={isPageSelectionOpen}
        onClose={() => setIsPageSelectionOpen(false)}
        totalStudents={getTotalStudents()}
        onDownload={handlePrintList}
      />

      <StudentDownloadFormatDialog
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
        onDownload={handleDirectFormatDownload}
        totalStudents={getTotalStudents()}
      />
    </>
  );
};

export default StudentsHeader;
